import { useCallback } from 'react';
import graphql from 'babel-plugin-relay/macro';

import DescriptionIcon from '@mui/icons-material/Description';
import TableRow from '@mui/material/TableRow';
import Table from 'components/Table';
import TableHeadCell from 'components/TableHeadCell';
import TableCell from 'components/TableCell';
import TravelDocumentsListRow from 'components/TravelDocumentsListRow';
import FabDialog from 'components/FabDialog';

import { useAvoidSuspense, useDelete, useSkipFirstLoad } from 'lib';
import { useSearch } from 'components/Search';
import { usePagination } from 'components/Pagination';

import Query, {
  TravelDocumentsListRefetchQuery,
} from '__generated__/TravelDocumentsListRefetchQuery.graphql';
import { TravelDocumentsList_data$key } from '__generated__/TravelDocumentsList_data.graphql';
import TravelDocumentsListAdd from 'components/TravelDocumentsListAdd';

type Props = {
  data: TravelDocumentsList_data$key;
};

const TravelDocumentsList = ({ data }: Props) => {
  const { search } = useSearch();
  const { variables } = usePagination();

  const [{ travelDocuments }, { refetch, loading }] = useAvoidSuspense<
    TravelDocumentsListRefetchQuery,
    TravelDocumentsList_data$key
  >(
    graphql`
      fragment TravelDocumentsList_data on Query
      @refetchable(queryName: "TravelDocumentsListRefetchQuery")
      @argumentDefinitions(
        first: { type: "Int!" }
        after: { type: "String" }
        search: { type: "String" }
      ) {
        travelDocuments(first: $first, after: $after, search: $search) {
          __id
          edges {
            node {
              id

              ...TravelDocumentsListRow_document
            }
          }

          pageInfo {
            ...Table_pageInfo
          }
        }
      }
    `,
    data,
    Query
  );

  const connectionId = travelDocuments?.__id;

  const [deleteDocument, deleting] = useDelete(connectionId);

  const { edges, pageInfo } = travelDocuments ?? {};

  useSkipFirstLoad(
    useCallback(() => {
      refetch({
        ...variables,
        search,
      });
    }, [refetch, search, variables])
  );

  return (
    <>
      <Table
        search
        pagination
        pageInfo={pageInfo ?? null}
        icon={<DescriptionIcon />}
        loading={loading || deleting}
        head={
          <TableRow>
            <TableHeadCell name="name">Pavadinimas</TableHeadCell>

            <TableCell />
          </TableRow>
        }>
        {edges?.map(({ node }) => (
          <TravelDocumentsListRow
            key={node.id}
            document={node}
            onDelete={deleteDocument}
          />
        ))}
      </Table>

      {connectionId && (
        <FabDialog fullWidth maxWidth="sm">
          <TravelDocumentsListAdd connectionId={connectionId} />
        </FabDialog>
      )}
    </>
  );
};

export default TravelDocumentsList;
